import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'TR' | 'EN';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    // Initialize language from localStorage or default to 'TR'
    const [language, setLanguageState] = useState<Language>(() => {
        const stored = localStorage.getItem('language');
        return (stored === 'TR' || stored === 'EN') ? stored : 'TR';
    });

    // Persist language preference to localStorage
    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem('language', lang);
    };

    useEffect(() => {
        // Sync with localStorage on mount
        const stored = localStorage.getItem('language');
        if (stored === 'TR' || stored === 'EN') {
            setLanguageState(stored);
        }
    }, []);

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};
